/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { RouteComponentProps } from '@reach/router';
import { lighten } from 'polished';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import Button from '../../components/Button2';
import { Column, Row } from '../../components/Flex';
import Loader from '../../components/loader/Loader';
import { Modal, ModalTrigger } from '../../components/Modal';
import AuthedPageLayout from '../../components/pagelayout/AuthedPageLayout';
import { Selector } from '../../components/Selector';
import Text from '../../components/Text';
import { useT } from '../../i18n/useT';
import { useAddNotification } from '../../lib/notifications/NotificationContext';
import useSelectedPatientId from '../../lib/useSelectedPatientId';
import MedicalConditionClient from '../../network/MedicalConditionClient';
import { useQueryWithLogHistory } from '../../network/useQueryWithLogHistory';
import { buttonReset } from '../../utils/styles';
import theme from '../../utils/theme';
import { VersionedText } from '../../VersionedText';
import { HistorySelector } from '../medication/HistorySelector';

const MedicalConditions = () => {
  const t = useT();
  const selectedPatientId = useSelectedPatientId()!;

  const [query, logHistory, refetch] = useQueryWithLogHistory(
    ['fetchMedicalConditions', selectedPatientId],
    `/Condition/Patient/${selectedPatientId}`
  );

  const conditions = query.data?.conditions ?? [];

  return (
    <div>
      <h1>{t('Medical conditions')}</h1>
      <div style={{ height: 20 }} />

      {query.isLoading && <Loader />}

      {!query.isLoading && conditions.length === 0 && (
        <Text size="large">{t("You haven't added any medical conditions yet.")}</Text>
      )}

      {conditions.map((condition) => (
        <section
          key={condition.id}
          css={css`
            background-color: #f5f7fb;
            box-shadow: ${theme.shadows.block};
            margin-bottom: 8px;
            border-radius: 8px;
            &:hover {
              background-color: ${theme.colors.grayBackground};
            }
          `}
        >
          <Row
            horizontal="space-between"
            css={css`
              @media (max-width: 599px) {
                flex-direction: column;
              }
            `}
          >
            <Column
              css={css`
                padding: 16px;
              `}
            >
              <VersionedText size="large" crudObject={condition.code}>
                {condition.code.translatedText}
              </VersionedText>
            </Column>

            {!logHistory.pointInTime && (
              <button
                onClick={() => {
                  const result = window.confirm('Slett klinisk forhold?');
                  if (result) {
                    MedicalConditionClient.deleteMedicalCondition(condition.id).then(() => refetch());
                  }
                }}
                css={[
                  buttonReset,
                  css`
                    background-color: ${lighten(0.05, theme.colors.blue)};
                    color: #ffffff;
                    padding: 16px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                    text-decoration: none;
                    border-top-right-radius: 8px;
                    border-bottom-right-radius: 8px;

                    @media (max-width: 599px) {
                      border-top-right-radius: 0px;
                      border-bottom-right-radius: 8px;
                      border-bottom-left-radius: 8px;
                    }

                    &:hover {
                      background-color: ${lighten(0.4, theme.colors.blue)};
                      color: #000000;
                    }
                  `,
                ]}
              >
                <Text invert>{t('Remove')}</Text>
              </button>
            )}
          </Row>
        </section>
      ))}

      {!logHistory.pointInTime && (
        <>
          <div style={{ height: 20 }} />

          <ModalTrigger
            button={({ openModal }) => <Button onClick={openModal}>{t('Add new medical condition')}</Button>}
            modal={({ closeModal }) => (
              <CreateMedicalConditionModal closeModal={closeModal} onSuccess={() => refetch()} />
            )}
          />
        </>
      )}

      <div style={{ height: 20 }} />

      <HistorySelector logHistory={logHistory} />
    </div>
  );
};

const CreateMedicalConditionModal = ({ closeModal, onSuccess }: { closeModal: () => void; onSuccess: () => void }) => {
  const { i18n } = useTranslation();
  const t = useT();
  const { addError, addInfo } = useAddNotification();
  const selectedPatientId = useSelectedPatientId()!;
  const [value, setValue] = useState<string>('');

  const availableMedicalConditionsQuery = useQuery(['availableMedicalConditions', i18n.language], () =>
    MedicalConditionClient.fetchAvailableMedicalConditions()
  );

  const availableConditions = availableMedicalConditionsQuery.data?.data?.coding ?? [];

  const selectedItem = availableConditions.find((item) => item.code === value);

  const onSubmit = async () => {
    if (!selectedItem) {
      return;
    }

    try {
      const response = await MedicalConditionClient.createMedicalCondition({
        patientId: selectedPatientId,
        type: selectedItem!,
      });

      if (response.success) {
        onSuccess();
        closeModal();
        addInfo({ message: 'Added' });
      } else {
        addError({ message: (response.error as any)?.resultMessage ?? 'Unknown error' });
      }
    } catch (error) {
      addError({ message: 'Unknown error' });
    }
  };

  return (
    <Modal title={t('New medical condition')} closeModal={closeModal}>
      <Selector
        title={t('Medical condition')}
        value={value}
        onValue={(val) => setValue(val)}
        data={[
          { value: '', label: t('Choose from list...'), disabled: true },
          ...availableConditions.map((item) => ({ value: item.code, label: item.display })),
        ]}
      />
      <div style={{ height: 20 }} />
      <Button onClick={onSubmit}>{t('Add')}</Button>
    </Modal>
  );
};

export const MedicalConditionsPage: FC<RouteComponentProps> = () => {
  const t = useT();

  return (
    <AuthedPageLayout width="narrow" documentTitle={t('Capable - Medical conditions')}>
      <MedicalConditions />
    </AuthedPageLayout>
  );
};
